// 履歴の最大保持件数
const HISTORY_SIZE = 5;

// 差分結果用定数
const DIFF_STATUS = {
    NONE: Symbol(0), // 差分なし
    DIFF: Symbol(1), // 差分あり
    DIFF_STATIC_HTML_ONLY: Symbol(2), // 変更ではなくstatic_html再計算の結果差分が発生した場合(編集履歴の現在を更新する必要あり)
};

/**
 * 編集履歴を貯める際の差分チェック処理
 * object内全階層の値をチェックする
 *
 * @param {object} a - 比較したいオブジェクト
 * @param {object} b - 比較したいオブジェクト
 * @returns {DIFF_STATUS}
 */
function getDiffStatus(a, b) {
    // object同士の比較以外がきた場合にはじく
    const [ type_a, type_b ] = [a, b].map(v => typeof v);
    if (type_a !== 'object' || type_a !== type_b) return DIFF_STATUS.DIFF;

    // 差分があったキー配列を取得
    const diff_keys = getDiffKeys(a, b);

    // 差分がない場合ははじく
    if (diff_keys.length <= 0) return DIFF_STATUS.NONE;

    return (diff_keys.every((key) => (['static_html', 'usage_rate'].indexOf(key) > -1)) ? DIFF_STATUS.DIFF_STATIC_HTML_ONLY : DIFF_STATUS.DIFF);
}

/**
 * 差分のあるキーを配列で取得する
 * getDiffStatus()から呼び出す子処理
 *
 * @param {object|array} a
 * @param {object|array} b
 * @returns {array}
 */
function getDiffKeys(a, b) {
    let diff_keys = [];
    // object以外の場合は比較できるように整形
    const [ obj_a, obj_b ] = [a, b].map(v => {
        // nullの場合は空objectに変換
        if (v === null) return {};

        // 配列の場合はobjectに変換
        if (Array.isArray(v)) {
            return v.reduce((obj, current, i) => { return {...obj, [i]: current}; }, {});
        }
        return v;
    });

    // 共通するキー、片方にしか存在しないキーを分類して、片方にしか存在しないキーは差分に追加
    const [ keys_a, keys_b ] = [obj_a, obj_b].map(v => Object.keys(v));
    const { keys_common, keys_diff } = [...new Set(keys_a.concat(keys_b))].reduce((obj, current) => {
        obj[([keys_a, keys_b].every(arr => arr.indexOf(current) > -1) ? 'keys_common' : 'keys_diff')].push(current);
        return obj;
    }, { keys_common: [], keys_diff: [] });
    diff_keys.push(...keys_diff);

    // 共通するキーの差分をチェック、配列やobjectの場合は再帰的にチェック
    diff_keys.push(...keys_common.reduce((diff_arr, key) => {
        // 配列・オブジェクトの場合は再帰的にチェック
        if ([obj_a, obj_b].every(obj => typeof obj[key] === 'object')) return [...diff_arr, ...getDiffKeys(obj_a[key], obj_b[key])];
        // それ以外の場合は値チェック
        if (obj_a[key] === obj_b[key] ||
            [obj_a, obj_b].every(obj => ([null, ''].indexOf(obj[key]) > -1))) { // nullが空文字に変換される場合もあるがstatic_html更新時にnullへと統一されるため差分なしとする
            return diff_arr;
        }

        return [...diff_arr, key];
    }, []));

    return diff_keys;
}

/**
 * Undo / Redo用コンポーネント
 */
export default {
    template: '#edit_history_template',
    data() {
        return {
            history: {},
            current_index_data: {},

            is_undo_able: false,
            is_redo_able: false,
        };
    },
    props: {
        // 編集ブロック
        edit_area: '',
        // 編集データ
        edit_data: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        // 編集ブロック
        editArea: function() {
            return (this.edit_area ?? '');
        },
        // 編集データ(このコンポーネント用)
        editData: {
            get() {
                return this.edit_data;
            },
            set(value) {
                this.$emit('update:edit_data', value);
            },
        },
    },
    watch: {
        // 編集データの変更検知
        edit_data: function (value) {
            this.addHistory(value);
        },
    },
    created: function () {
        this.initHistory();
    },
    emits: [
        'update:edit_data',
        'emit-exec-undoredo',
    ],
    methods: {
        // 初期化処理
        initHistory: function () {
            this.history = {};
            this.current_index_data = {};
            this.updateBtnEnabled();

            // 現状のデータをセット
            this.addHistory(this.edit_data);
        },

        // Undo / Redoボタンの有効フラグ更新
        updateBtnEnabled: function () {
            this.is_undo_able = (this.getCurrentIndex() > 0);
            this.is_redo_able = (this.getCurrentIndex() < (this.getHistory().length - 1));
        },

        // 現在の履歴インデックスを取得する
        getCurrentIndex: function () {
            return this.current_index_data[this.editArea] ?? 0;
        },
        // 編集履歴の取得
        getHistory: function () {
            return this.history[this.editArea] ?? [];
        },

        // 編集履歴の追加
        addHistory: function (data) {
            // パーツが空でもここに空オブジェクトがくることはないためはじく
            if (typeof data !== 'object' || Object.keys(data).length <= 0) return;

            // 変更チェック
            const diff_status = getDiffStatus(JSON.parse(this.getHistory()[this.getCurrentIndex()] ?? '{}'), data);
            switch (diff_status) {
                // 変更がない場合
                case DIFF_STATUS.NONE: break;

                // 変更を加えた場合
                case DIFF_STATUS.DIFF:
                    // Undo後に変更を行った場合は、現在の履歴以降を削除して追加する
                    this.history[this.editArea] = this.getHistory().slice(0, (this.getCurrentIndex() + 1)).concat(JSON.stringify(data));

                    // 履歴の最大保持件数を越えた場合は古いものから削除
                    let historyOverNum = this.getHistory().length - HISTORY_SIZE;
                    if (historyOverNum > 0) {
                        this.history[this.editArea].splice(0, historyOverNum);
                    }

                    // 現在のインデックスを末尾に合わせる
                    this.current_index_data[this.editArea] = this.getHistory().length - 1;
                    break;

                // static_htmlの再計算が行われた場合
                case DIFF_STATUS.DIFF_STATIC_HTML_ONLY:
                    // 現在の履歴を最新に上書きして終了
                    this.history[this.editArea][this.getCurrentIndex()] = JSON.stringify(data);
                    break;

                default:
                    console.error(`error: DIFF_STATUS(${diff_status})`);
                    break;
            }

            // ボタンの更新処理
            this.updateBtnEnabled();
        },
        // 保存時履歴削除処理
        clearHistory: function () {
            this.initHistory();
        },

        // Undo実行
        execUndo: function () {
            this.editData = JSON.parse(this.history[this.editArea][--this.current_index_data[this.editArea]]);

            this.updateBtnEnabled();
            this.emitExecUndoRedo();
        },
        // Redo実行
        execRedo: function () {
            this.editData = JSON.parse(this.history[this.editArea][++this.current_index_data[this.editArea]]);

            this.updateBtnEnabled();
            this.emitExecUndoRedo();
        },

        // -------------------------
        // emit
        // -------------------------
        // Undo / Redo実行完了コールバック
        // StaticHtmlEditor::getStaticHtml()をコールするためのトリガー
        emitExecUndoRedo: function () {
            this.$emit('emit-exec-undoredo');
        },
    }
};
